import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { orderBy } from 'lodash/collection';
import { isEmpty } from 'lodash/lang';

import PatientInfoPageApi from '../../../../../../../api/patient/PatientInfoPageApi';
import { LoadingHandler } from '../../../../../../../common/elements/Loader/LoadingHandler';
import Button from '../../../../../../../common/general/Button';
import { VIEW_ALL_LOGS } from '../../../../../../../constants/accessToPages';
import { VIEW_LOG_DATA } from '../../../../../../../constants/userOperations';
import { userHasAccessTo } from '../../../../../../../services/auth';
import { NS_COMMON, NSPP_LOGS } from '../../../../../../../services/i18n/namespaces';
import AccessDeniedTooltip from '../../../../../../AccessDeniedTooltip/AccessDeniedTooltip';
import { useCurrentUser } from '../../../../../../root/Container/CurrentUserContainer';
import { generateUrlByKey, useCurrentRoute } from '../../../../../../root/router';
import WithMultiRoleCheck from '../../../../../../UserRoleSwitcher/WithMultiRoleCheck';

import './LogsWidget.scss';

export default function LogsWidget({ ssuPatientId }) {
  const { t } = useTranslation();
  const currentRoute = useCurrentRoute();
  const { accessToPages } = useCurrentUser();
  const [logs, setLogs] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(
    function() {
      if (!ssuPatientId) {
        return;
      }
      setLoading(true);
      PatientInfoPageApi.getSitePatientLogs(ssuPatientId).then(function(res) {
        setLogs(orderBy(res.data.logItemGroups, [log => log.name.toLowerCase()], ['asc']));
        setLoading(false);
      });
    },
    [ssuPatientId]
  );

  return (
    <div className="eds-logs-widget">
      <div className="elw-header">
        <h3>{t(`${NSPP_LOGS}:logs`)}</h3>
        {accessToPages.includes(VIEW_ALL_LOGS) && (
          <Button size="h28" priority="low" to="all-logs">
            {t(`${NS_COMMON}:viewAll`)}
          </Button>
        )}
      </div>
      <div className="elw-list">
        <LoadingHandler complete={!loading}>
          {isEmpty(logs) ? (
            <div className="elw-list-no-data">{t(`${NSPP_LOGS}:thereAreNoLogs`)}</div>
          ) : (
            logs.map(function({ name, itemGroupTemplateId, protocolStatus, openStatusRowsCount }) {
              const displayName = name + (protocolStatus === 'Retired' ? t(`${NSPP_LOGS}:old`) : '');
              const count = openStatusRowsCount ? t(`${NSPP_LOGS}:open`, { value: openStatusRowsCount }) : null;
              if (!userHasAccessTo(VIEW_LOG_DATA)) {
                return (
                  <div key={itemGroupTemplateId} className={'elw-list-item'}>
                    <AccessDeniedTooltip showContentOnly={true} placement="right" align={{ offset: [20, 0] }}>
                      <span>{displayName}</span>
                    </AccessDeniedTooltip>
                  </div>
                );
              } else {
                return (
                  <WithMultiRoleCheck
                    Component={Link}
                    ssuPatientId={currentRoute.params.ssuPatientId}
                    to={generateUrlByKey(`${currentRoute?.key}.Logs`, {
                      patientId: currentRoute.params.patientId,
                      ssuPatientId: currentRoute.params.ssuPatientId,
                      patientItemGroupId: itemGroupTemplateId
                    })}
                    replace={true}
                    className="elw-list-item"
                    key={itemGroupTemplateId}
                  >
                    {displayName}
                    {count ? ' - ' : ''} <span className="elw-list-item-count">{count ? count : ''}</span>
                  </WithMultiRoleCheck>
                );
              }
            })
          )}
        </LoadingHandler>
      </div>
    </div>
  );
}
